import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery, Tooltip } from '@mui/material';

import config from '../../../../../config';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { menuOpen, setMenu } from 'store/slices/customizationSlice';

const NavItem = ({ item, level, open }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

    const Icon = item.icon;
    const itemIcon = Icon ? (
        <Icon sx={{ fontSize: 18 }} />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: customization?.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
                height: customization?.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    const itemTarget = item.target ? '_blank' : '_self';

    let listItemProps = {
        component: forwardRef((props, ref) => (
            <Link ref={ref} {...props} to={`${config.basename}${item.url}`} target={itemTarget} />
        ))
    };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = (id) => {
        dispatch(menuOpen({ id }));
        if (matchesSM) dispatch(setMenu({ opened: false }));
    };

    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            dispatch(menuOpen({ id: item.id }));
        }
    }, [dispatch, item.id]);

    const button = (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            sx={{
                borderRadius: `${customization?.borderRadius}px`,
                mb: 0.5,
                alignItems: 'flex-start',
                backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
                py: 1,
                pl: open ? `${level * 24}px` : 1,
                justifyContent: open ? 'initial' : 'center'
            }}
            selected={customization?.isOpen.findIndex((id) => id === item?.id) > -1}
            onClick={() => itemHandler(item.id)}
        >
            <ListItemIcon
                sx={{
                    my: 'auto',
                    minWidth: open ? (!item?.icon ? 18 : 36) : 0,
                    mr: open ? 1.5 : 0,
                    justifyContent: 'center'
                }}
            >
                {itemIcon}
            </ListItemIcon>

            <ListItemText
                primary={<Typography variant="h6" color="inherit">{item.title}</Typography>}
                secondary={
                    item.caption && open && (
                        <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                            {item.caption}
                        </Typography>
                    )
                }
                sx={{ opacity: open ? 1 : 0 }}
                primaryTypographyProps={{ noWrap: true }}
            />

            {item.chip && open && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );

    return open ? (
        button
    ) : (
        <Tooltip title={item.title} placement="right">
            {button}
        </Tooltip>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number,
    open: PropTypes.bool
};

export default NavItem;